import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Spinner } from '@/components/common/Spinner';
import { ErrorState } from '@/components/common/ErrorState';
import { AsyncButton } from '@/components/common/AsyncButton';
import { useToast } from '@/components/common/toast';
import { ApiError } from '@/api/client/abpError';
import {
  featureManagementApi,
  isOn,
  isSelection,
  isToggle,
  selectionItems,
  type FeatureDto,
  type FeatureGroupDto,
} from '@/api/services/featureManagement';

const HIDDEN_GROUPS = new Set(['SettingManagement', 'Emailing']);

// Subscribed Products = ABP Feature Management for the tenant (provider "T").
// Layout mirrors the LEGACY TenantSubscribedProducts.cshtml: a flat wrap of
// col-lg-3 bordered group boxes, each listing its features as CHECKBOXES (not
// switches) with no tabs. Cancel + Save at the top.
export function SubscribedProductsTab({ tenantId }: { tenantId: string }) {
  const toast = useToast();
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['tenant-features', tenantId],
    queryFn: () => featureManagementApi.getTenantFeatures(tenantId),
  });

  const [edits, setEdits] = useState<Record<string, string | null>>({});
  const dirty = Object.keys(edits).length > 0;

  const groups = useMemo<FeatureGroupDto[]>(
    () => (query.data?.groups ?? []).filter((g) => !HIDDEN_GROUPS.has(g.name)),
    [query.data],
  );

  const values = useMemo(() => {
    const base: Record<string, string | null> = {};
    (query.data?.groups ?? []).forEach((g) => g.features.forEach((f) => (base[f.name] = f.value)));
    return { ...base, ...edits };
  }, [query.data, edits]);

  const save = useMutation({
    mutationFn: () =>
      featureManagementApi.updateTenantFeatures(
        tenantId,
        Object.entries(values).map(([name, value]) => ({ name, value })),
      ),
    onSuccess: () => {
      toast.success('Subscribed products saved.');
      setEdits({});
      void qc.invalidateQueries({ queryKey: ['tenant-features', tenantId] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to save.'),
  });

  function setValue(name: string, value: string | null) {
    setEdits((prev) => ({ ...prev, [name]: value }));
  }

  // Toggle cascade within a group (mirrors feature-management-modal.js):
  // checking a feature checks all its parents; unchecking unchecks all descendants.
  function setToggle(group: FeatureGroupDto, feature: FeatureDto, checked: boolean) {
    const byName = new Map(group.features.map((f) => [f.name, f]));
    const byParent = new Map<string, FeatureDto[]>();
    group.features.forEach((f) => {
      if (f.parentName) {
        const list = byParent.get(f.parentName) ?? [];
        list.push(f);
        byParent.set(f.parentName, list);
      }
    });

    setEdits((prev) => {
      const next = { ...prev };
      next[feature.name] = checked ? 'true' : 'false';
      if (checked) {
        let p = feature.parentName ?? null;
        while (p && byName.has(p)) {
          next[p] = 'true';
          p = byName.get(p)!.parentName ?? null;
        }
      } else {
        const uncheckDescendants = (name: string) => {
          (byParent.get(name) ?? []).forEach((child) => {
            next[child.name] = 'false';
            uncheckDescendants(child.name);
          });
        };
        uncheckDescendants(feature.name);
      }
      return next;
    });
  }

  if (query.isLoading) return <Spinner />;
  if (query.isError)
    return <ErrorState message="Failed to load subscribed products." onRetry={() => query.refetch()} />;

  if (groups.length === 0) {
    return <div className="my-2">No features are available for this client.</div>;
  }

  const depthWithin = (group: FeatureGroupDto, f: FeatureDto) => {
    const byName = new Map(group.features.map((x) => [x.name, x]));
    let d = 0;
    let p = f.parentName;
    while (p && byName.has(p)) {
      d++;
      p = byName.get(p)!.parentName ?? null;
    }
    return d;
  };

  return (
    <div>
      <div className="d-flex justify-content-end gap-2 mb-3">
        <button type="button" className="btn btn-dark" disabled={save.isPending || !dirty} onClick={() => setEdits({})}>
          Cancel
        </button>
        <AsyncButton className="btn btn-primary" icon="fa fa-check" loading={save.isPending} disabled={!dirty} onClick={() => save.mutateAsync()}>
          Save
        </AsyncButton>
      </div>

      <div className="subscribed-products-groups d-flex flex-wrap">
        {groups.map((group) => (
          <div className="col-lg-3" key={group.name}>
            <div className="h-100 border mb-3 px-4 py-2">
              <div className="pt-1">
                {group.features.map((f) => {
                  const depth = depthWithin(group, f);
                  if (isToggle(f)) {
                    const indent = { marginInlineStart: depth * 20 };
                    return (
                      <div className="mt-2" key={f.name} style={indent}>
                        <div className="form-check custom-checkbox mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`feat-${f.name}`}
                            checked={isOn(values[f.name])}
                            onChange={(e) => setToggle(group, f, e.target.checked)}
                          />
                          <label className="form-check-label" htmlFor={`feat-${f.name}`}>
                            {f.displayName}
                          </label>
                        </div>
                        {f.description && <div className="form-text mt-0 mb-1">{f.description}</div>}
                      </div>
                    );
                  }
                  const indent = { marginInlineStart: depth * 25 };
                  return (
                    <div className="mb-3 mt-2" key={f.name} style={indent}>
                      <label className="form-label" htmlFor={`feat-${f.name}`}>
                        {f.displayName}
                      </label>
                      {isSelection(f) && selectionItems(f).length > 0 ? (
                        <select
                          id={`feat-${f.name}`}
                          className="form-select"
                          value={values[f.name] ?? ''}
                          onChange={(e) => setValue(f.name, e.target.value)}
                        >
                          {selectionItems(f).map((it) => (
                            <option key={it.value} value={it.value}>
                              {it.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          id={`feat-${f.name}`}
                          className="form-control"
                          value={values[f.name] ?? ''}
                          onChange={(e) => setValue(f.name, e.target.value)}
                        />
                      )}
                      {f.description && <div className="form-text">{f.description}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
